/**
 * Basal metabolic rate by published equation (B-0137): Mifflin-St Jeor (default), Harris-Benedict (revised) and Katch-McArdle (needs body fat). Pure; synced to
 * the API (/v1/converters/bmr). calorie.ts `bmrMifflin` is unchanged and a test holds the Mifflin result here to it.
 *
 *  - Mifflin-St Jeor: 10 x kg + 6.25 x cm - 5 x age + 5 (men) or - 161 (women). Mifflin MD, St Jeor ST, Hill LA, Scott BJ, Daugherty SA, Koh YO.
 *    "A new predictive equation for resting energy expenditure in healthy individuals." Am J Clin Nutr 1990;51(2):241-247.
 *  - Harris-Benedict, revised by Roza and Shizgal: men 88.362 + 13.397 x kg + 4.799 x cm - 5.677 x age; women 447.593 + 9.247 x kg + 3.098 x cm - 4.330 x age.
 *    Roza AM, Shizgal HM. "The Harris Benedict equation reevaluated: resting energy requirements and the body cell mass." Am J Clin Nutr 1984;40(1):168-182.
 *    Coefficients checked on 30 Oct 2026 against the Dutch national guideline database table (richtlijnendatabase.nl). The ORIGINAL 1919 Harris-Benedict
 *    constants are deliberately not offered: they were not re-checked.
 *  - Katch-McArdle: 370 + 21.6 x lean body mass in kg, where lean body mass = weight x (1 - body fat %). From McArdle, Katch and Katch, Exercise Physiology
 *    (the textbook; the page number was not checked). It needs a body-fat figure and ignores sex, age and height.
 * All three are estimates of resting energy use in healthy adults. They differ by a few percent, and body fat from a scale or calipers is itself an estimate.
 */
import type { CalcStep } from "@/lib/calc/steps";

export type BmrFormula = "mifflin" | "harris" | "katch";
export type Sex = "male" | "female";
export const BMR_FORMULA_ORDER: BmrFormula[] = ["mifflin", "harris", "katch"];

export const BMR_FORMULAS: Record<BmrFormula, { name: string; equation: (sex: Sex) => string; citation: string; needsBodyFat: boolean }> = {
  mifflin: {
    name: "Mifflin-St Jeor", needsBodyFat: false,
    equation: (sex) => `10 x kg + 6.25 x cm - 5 x age ${sex === "male" ? "+ 5" : "- 161"}`,
    citation: "Mifflin MD, St Jeor ST, et al. Am J Clin Nutr 1990;51(2):241-247",
  },
  harris: {
    name: "Harris-Benedict (revised 1984)", needsBodyFat: false,
    equation: (sex) => (sex === "male" ? "88.362 + 13.397 x kg + 4.799 x cm - 5.677 x age" : "447.593 + 9.247 x kg + 3.098 x cm - 4.330 x age"),
    citation: "Roza AM, Shizgal HM. Am J Clin Nutr 1984;40(1):168-182 (revision of Harris and Benedict, 1919)",
  },
  katch: {
    name: "Katch-McArdle", needsBodyFat: true,
    equation: () => "370 + 21.6 x lean body mass (kg), where lean body mass = weight x (1 - body fat %)",
    citation: "McArdle WD, Katch FI, Katch VL. Exercise Physiology (textbook; page not checked)",
  },
};

/** Sanity range for a typed body-fat percentage (a data-entry check, not a medical limit). */
export const BODY_FAT_RANGE = { min: 3, max: 60 };
export const bodyFatError = (pct: number): string => (Number.isFinite(pct) && pct >= BODY_FAT_RANGE.min && pct <= BODY_FAT_RANGE.max ? "" : `Enter body fat between ${BODY_FAT_RANGE.min}% and ${BODY_FAT_RANGE.max}%.`);

export const leanMassKg = (weightKg: number, bodyFatPct: number) => weightKg * (1 - bodyFatPct / 100);

export interface BmrInput { sex: Sex; weightKg: number; heightCm: number; age: number; bodyFatPct?: number }

/** Unrounded BMR in kcal per day, or null when Katch-McArdle is asked for without a valid body-fat figure. */
export function bmrByFormula(f: BmrFormula, i: BmrInput): number | null {
  if (f === "mifflin") return 10 * i.weightKg + 6.25 * i.heightCm - 5 * i.age + (i.sex === "male" ? 5 : -161);
  if (f === "harris") return i.sex === "male" ? 88.362 + 13.397 * i.weightKg + 4.799 * i.heightCm - 5.677 * i.age : 447.593 + 9.247 * i.weightKg + 3.098 * i.heightCm - 4.33 * i.age;
  if (i.bodyFatPct === undefined || bodyFatError(i.bodyFatPct)) return null;
  return 370 + 21.6 * leanMassKg(i.weightKg, i.bodyFatPct);
}

export type FormulaChoice = "auto" | BmrFormula;
export interface Resolved { formula: BmrFormula; reason: "default" | "chosen" | "body-fat-entered" | "body-fat-needed" }
/**
 * "auto" (the default) is Mifflin-St Jeor, or Katch-McArdle once a valid body-fat figure is entered. Choosing Katch-McArdle with no body fat falls back to
 * Mifflin-St Jeor and says so (reason "body-fat-needed").
 */
export function resolveFormula(choice: FormulaChoice, bodyFatPct: number | undefined): Resolved {
  const hasBf = bodyFatPct !== undefined && !bodyFatError(bodyFatPct);
  if (choice === "auto") return hasBf ? { formula: "katch", reason: "body-fat-entered" } : { formula: "mifflin", reason: "default" };
  if (choice === "katch" && !hasBf) return { formula: "mifflin", reason: "body-fat-needed" };
  return { formula: choice, reason: "chosen" };
}

export interface FormulaRow { formula: BmrFormula; name: string; bmr: number | null; equation: string }
/** Every formula that can be worked out from the inputs, side by side (Katch-McArdle only with body fat). */
export function compareFormulas(i: BmrInput): FormulaRow[] {
  return BMR_FORMULA_ORDER.map((f) => ({ formula: f, name: BMR_FORMULAS[f].name, bmr: bmrByFormula(f, i), equation: BMR_FORMULAS[f].equation(i.sex) })).filter((r) => r.bmr !== null);
}

/** The working for the formula used. `fmt` formats numbers. */
export function bmrSteps(f: BmrFormula, i: BmrInput, activity: number | null, fmt: (n: number) => number | string = (n) => Math.round(n * 100) / 100): CalcStep[] {
  const info = BMR_FORMULAS[f], bmr = bmrByFormula(f, i);
  const steps: CalcStep[] = [];
  if (f === "katch" && i.bodyFatPct !== undefined) steps.push({ label: "Lean body mass", formula: "Weight x (1 - body fat % / 100)", inputs: { "weight (kg)": i.weightKg, "body fat %": i.bodyFatPct }, result: fmt(leanMassKg(i.weightKg, i.bodyFatPct)) });
  steps.push({ label: `BMR (${info.name})`, formula: info.equation(i.sex), inputs: f === "katch" ? { "lean body mass (kg)": i.bodyFatPct === undefined ? "?" : fmt(leanMassKg(i.weightKg, i.bodyFatPct)) } : { "weight (kg)": i.weightKg, "height (cm)": i.heightCm, "age": i.age, sex: i.sex }, result: bmr === null ? "needs body fat" : fmt(bmr) });
  if (activity !== null && bmr !== null) steps.push({ label: "Maintenance calories (BMR x activity)", formula: "BMR x activity factor", inputs: { BMR: fmt(bmr), "activity factor": activity }, result: fmt(bmr * activity) });
  return steps;
}
